import React, { useContext, useEffect, useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { Box, Flex, Heading, Text } from '@radix-ui/themes';
import { Cross2Icon } from '@radix-ui/react-icons';
import Button from '@mui/material/Button';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { smartHubApi } from '../../services/api';
import { useDwellTime } from '../../hooks/useDwellTime';
import { AuthContext } from '../../contexts/AuthContext';
import { PATIENT_DETAIL_TAB_INDEX } from '../PatientDetail';
import type { SmartHubItem, SmartHubSubmissionGroup } from './types';
import { isAnswer, isEhrChange, isReading, isTask } from './types';
import { formatTaskCalendarDay } from './TaskBar';

type AckTarget = SmartHubItem | SmartHubSubmissionGroup;

const isSubmissionGroup = (t: AckTarget): t is SmartHubSubmissionGroup =>
  t.source_kind === 'submission';

// PHA-480: post-ack "View in chart" routes by item type. Tab indices come
// from PatientDetail.tsx so a future reorder updates both places at once.
const tabForTarget = (t: AckTarget): number => {
  if (isSubmissionGroup(t)) return PATIENT_DETAIL_TAB_INDEX.ANSWERS;
  if (isReading(t)) return PATIENT_DETAIL_TAB_INDEX.DEVICE_READINGS;
  if (isTask(t)) return PATIENT_DETAIL_TAB_INDEX.CARE_PLANS;
  // Single survey answer → Answers tab.
  return PATIENT_DETAIL_TAB_INDEX.ANSWERS;
};

const targetSubject = (t: AckTarget): string => {
  if (isSubmissionGroup(t)) return 'survey response';
  if (isReading(t)) return 'reading';
  if (isTask(t)) return 'care-plan task';
  return 'answer';
};

interface AckDrawerProps {
  target: AckTarget | null;
  onClose: () => void;
  // Called after a successful ack so the parent can refetch the task bar.
  onAcknowledged: () => Promise<void> | void;
}

const formatValue = (value: unknown): string => {
  if (value == null) return '—';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'object') {
    const v = value as any;
    if ('systolic' in v && 'diastolic' in v) return `${v.systolic} / ${v.diastolic}`;
    return JSON.stringify(v, null, 2);
  }
  return String(value);
};

const AckDrawer: React.FC<AckDrawerProps> = ({
  target,
  onClose,
  onAcknowledged,
}) => {
  const open = target !== null;
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // PHA-480: after a successful ack we keep the drawer open so the user can
  // click through to the patient chart. The Acknowledge button hides; a
  // "View in chart" + Close pair takes its place.
  const [acked, setAcked] = useState(false);

  const ackable =
    !!target &&
    (isSubmissionGroup(target) || isAnswer(target) || isReading(target));

  // PHA-710: read session warning state to pause dwell while the modal is shown.
  // Use useContext directly (with null fallback) so this component remains safe
  // in test harnesses that don't wrap it in an AuthProvider.
  const authCtx = useContext(AuthContext);
  const isSessionWarning = authCtx?.isSessionWarning ?? false;

  const { dwellSecondsSnapshot } = useDwellTime({
    patientId: target?.patient_id ?? null,
    isActive: open,
    minimumSeconds: 5,
    skip: !target,
    // PHA-533: the canonical billing event for a Smart Hub ack is the fixed
    // 30s "Smart Hub Acknowledgement" row written server-side by the ack
    // endpoints. Letting this hook also POST /log_dwell on drawer close (or
    // pagehide) would write a parallel "Active Care Management Time" event
    // covering the same review window — both rows would land in the billing
    // report and double-count the time. Snapshot is still tracked locally so
    // we can pass `dwell_seconds` to the ack endpoints (audit only).
    logToServer: false,
    // PHA-710: pause dwell while session warning modal is shown or screen is locked.
    paused: isSessionWarning,
  });

  useEffect(() => {
    if (!open) {
      setError(null);
      setAcked(false);
    }
  }, [open]);

  const handleAcknowledge = async () => {
    // PHA-480: drop the dwell-time gate entirely — let the user acknowledge
    // immediately. Continue capturing dwell_seconds and forwarding to the
    // server so the audit trail still records how long they reviewed.
    if (!target || !ackable || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      const seconds = dwellSecondsSnapshot();
      if (isSubmissionGroup(target)) {
        await smartHubApi.acknowledgeSubmission({
          submission_id: target.submission_id,
          prompts: target.answers.map((a) => a.prompt),
          dwell_seconds: seconds,
        });
      } else if (isAnswer(target)) {
        await smartHubApi.acknowledgeAnswer({
          submission_id: target.submission_id,
          prompt: target.prompt,
          dwell_seconds: seconds,
        });
      } else if (isReading(target)) {
        await smartHubApi.acknowledgeReading({
          reading_id: target.reading_id,
          dwell_seconds: seconds,
        });
      }
      // PHA-480: do NOT close immediately — keep the drawer open with the
      // post-ack "View in chart" affordance. Refetch the feed so the row
      // disappears from the rail behind the drawer.
      await Promise.resolve(onAcknowledged());
      setAcked(true);
    } catch (e: any) {
      setError(e?.message || 'Failed to acknowledge.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleViewInChart = () => {
    if (!target) return;
    // PHA-480: chart navigation gated on a prior ack — only reachable from
    // here, never from the Smart Hub list directly.
    window.dispatchEvent(
      new CustomEvent('phanova:open-patient-detail', {
        detail: {
          patientId: target.patient_id,
          initialTab: tabForTarget(target),
        },
      }),
    );
    onClose();
  };

  const renderHeader = () => {
    if (!target) return null;
    if (isSubmissionGroup(target)) {
      return (
        <>
          <Heading size="3">Survey response</Heading>
          <Text size="2" color="gray">
            {target.answers.length} answer{target.answers.length === 1 ? '' : 's'} · submission {target.submission_id}
          </Text>
        </>
      );
    }
    if (isTask(target)) {
      return (
        <>
          <Heading size="3">Care-plan task</Heading>
          <Text size="2" color="gray">
            {target.task}
          </Text>
        </>
      );
    }
    if (isReading(target)) {
      return (
        <>
          <Heading size="3">{target.reading_type ?? 'Reading'}</Heading>
          <Text size="2" color="gray">
            Device {target.device_id ?? '—'}
          </Text>
        </>
      );
    }
    // PHA-510: ehr_change items are routed through the auto-ack-and-navigate
    // flow in TaskBar.tsx and never reach the AckDrawer. The check here is a
    // defensive narrow so TypeScript can correctly type the survey-answer
    // fallback below; it should never actually render.
    if (isEhrChange(target)) {
      return (
        <Heading size="3">EHR change</Heading>
      );
    }
    return (
      <>
        <Heading size="3">{target.prompt}</Heading>
        <Text size="2" color="gray">
          Survey answer · submission {target.submission_id}
        </Text>
      </>
    );
  };

  const renderBody = () => {
    if (!target) return null;
    if (isSubmissionGroup(target)) {
      return (
        <Flex direction="column" gap="3">
          {target.answers.map((a, idx) => (
            <Box
              key={`${a.submission_id}-${idx}`}
              style={{
                padding: 12,
                borderRadius: 6,
                border: '1px solid var(--gray-a4)',
                backgroundColor: 'var(--gray-a2)',
              }}
            >
              <Text size="1" color="gray" as="div">
                {a.prompt}
              </Text>
              <Text size="2" as="div" style={{ wordBreak: 'break-word' }}>
                {formatValue(a.value)}
              </Text>
            </Box>
          ))}
          <Box>
            <Text size="1" color="gray" as="div">
              Submitted
            </Text>
            <Text size="2" as="div">
              {target.submission_time
                ? new Date(target.submission_time).toLocaleString()
                : '—'}
            </Text>
          </Box>
        </Flex>
      );
    }
    if (isEhrChange(target)) {
      // Defensive — see renderTitle note. ehr_change items never reach the
      // AckDrawer in practice; this branch keeps the type narrow.
      return null;
    }
    if (isTask(target)) {
      return (
        <Flex direction="column" gap="2">
          <Box>
            <Text size="1" color="gray" as="div">
              Disease
            </Text>
            <Text size="2" as="div">
              {target.disease ?? '—'}
            </Text>
          </Box>
          <Box>
            <Text size="1" color="gray" as="div">
              Due
            </Text>
            <Text size="2" as="div">
              {formatTaskCalendarDay(target.due_at)}
            </Text>
          </Box>
          <Box>
            <Text size="1" color="gray" as="div">
              Status
            </Text>
            <Text size="2" as="div">
              {target.status ?? '—'}
            </Text>
          </Box>
          {target.duplicate_count && target.duplicate_count > 1 && (
            <Box>
              <Text size="1" color="gray" as="div">
                Care plans
              </Text>
              <Text size="2" as="div">
                Same task is on {target.duplicate_count} of this patient&apos;s care plans. Each is completed separately on the chart.
              </Text>
            </Box>
          )}
          <Text size="1" color="gray">
            Tasks are managed in the patient detail view — open the chart to mark them complete.
          </Text>
        </Flex>
      );
    }
    return (
      <Flex direction="column" gap="3">
        <Box>
          <Text size="1" color="gray" as="div">
            Value
          </Text>
          <Box
            style={{
              fontFamily: 'var(--code-font-family, monospace)',
              fontSize: 14,
              padding: 12,
              borderRadius: 6,
              border: '1px solid var(--gray-a4)',
              backgroundColor: 'var(--gray-a2)',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
            }}
          >
            {formatValue(target.value)}
          </Box>
        </Box>
        <Box>
          <Text size="1" color="gray" as="div">
            Recorded
          </Text>
          <Text size="2" as="div">
            {target.submission_time
              ? new Date(target.submission_time).toLocaleString()
              : '—'}
          </Text>
        </Box>
      </Flex>
    );
  };

  return (
    <Dialog.Root open={open} onOpenChange={(o) => !o && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(15, 23, 42, 0.32)',
            zIndex: 1000,
          }}
        />
        <Dialog.Content
          aria-describedby={undefined}
          style={{
            position: 'fixed',
            top: 0,
            right: 0,
            height: '100dvh',
            width: 'min(480px, 100vw)',
            backgroundColor: '#fff',
            boxShadow: '-12px 0 32px rgba(15, 23, 42, 0.18)',
            zIndex: 1001,
            display: 'flex',
            flexDirection: 'column',
            outline: 'none',
          }}
        >
          <Dialog.Title style={{ display: 'none' }}>Smart Hub item</Dialog.Title>
          {/* Header */}
          <Flex
            justify="between"
            align="start"
            style={{ padding: 16, borderBottom: '1px solid var(--gray-a4)' }}
          >
            <Box style={{ minWidth: 0, flex: 1 }}>
              {target && (
                <Text size="1" color="gray" as="div">
                  {target.patient_name || target.patient_id}
                </Text>
              )}
              {renderHeader()}
            </Box>
            <Dialog.Close asChild>
              <button
                aria-label="Close"
                style={{
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  padding: 4,
                  marginLeft: 8,
                }}
              >
                <Cross2Icon width={20} height={20} />
              </button>
            </Dialog.Close>
          </Flex>

          {/* Body — buttons live inside the scrollable area so they sit
              directly below the content instead of pinned to the bottom. */}
          <Box style={{ flex: 1, overflowY: 'auto', padding: 16 }}>
            {renderBody()}

            {target && !isSubmissionGroup(target) && isTask(target) && (
              <Flex
                justify="end"
                align="center"
                style={{
                  marginTop: 20,
                  paddingTop: 16,
                  borderTop: '1px solid var(--gray-a4)',
                  gap: 12,
                }}
              >
                <Button onClick={onClose} variant="outlined" size="small" color="inherit">
                  Close
                </Button>
                <Button
                  onClick={handleViewInChart}
                  variant="outlined"
                  size="small"
                  startIcon={<OpenInNewIcon />}
                >
                  View in chart
                </Button>
              </Flex>
            )}
            {ackable && (
              <Flex
                justify="end"
                align="center"
                wrap="wrap"
                style={{
                  marginTop: 20,
                  paddingTop: 16,
                  borderTop: '1px solid var(--gray-a4)',
                  gap: 12,
                }}
              >
                {error && (
                  <Text size="1" color="red" style={{ marginRight: 'auto' }}>
                    {error}
                  </Text>
                )}
                {acked ? (
                  <Flex direction="column" style={{ width: '100%', gap: 12 }}>
                    <Text size="1" color="green">
                      ✓ {target ? targetSubject(target) : 'item'} acknowledged.
                    </Text>
                    <Flex justify="end" style={{ gap: 12 }}>
                      <Button onClick={onClose} variant="outlined" size="small" color="inherit">
                        Close
                      </Button>
                      <Button
                        onClick={handleViewInChart}
                        variant="outlined"
                        size="small"
                        startIcon={<OpenInNewIcon />}
                      >
                        View in chart
                      </Button>
                    </Flex>
                  </Flex>
                ) : (
                  <>
                    <Button onClick={onClose} variant="outlined" size="small" color="inherit">
                      Close
                    </Button>
                    <Button
                      onClick={handleAcknowledge}
                      disabled={submitting}
                      variant="contained"
                      size="small"
                      color="success"
                    >
                      {submitting
                        ? 'Acknowledging…'
                        : target && isSubmissionGroup(target)
                          ? `Acknowledge all (${target.answers.length})`
                          : 'Acknowledge'}
                    </Button>
                  </>
                )}
              </Flex>
            )}
          </Box>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

export default AckDrawer;
